/** Deal drawer — Contractors & Payments, v1 txn-expenses.js contractorsSection
 * parity (rebuilt 2026-07-19 against Tom's v1 screenshot): an always-open card
 * with the "+ Add Vendor" button, the four stat tiles (Active / Completed /
 * Quoted / Vendors), and per-vendor cards — name + type + status + payment-info
 * pills, progress bar with % and paid/total, remaining line, the emerald
 * payment log, and the Payment / CO buttons. One-click vendor delete (v1
 * delVendor has no confirm). Payments can settle an installment from the plan
 * (picked inside the payment form, exactly v1's Add-Payment modal) and mirror
 * a PAID row into the deal's expense ledger. Writes replace the WHOLE
 * TXN_CONTRACTORS array through the settings door; gated by liveFire
 * `contractorsEdit`. */
import { useMemo, useRef, useState, type ReactNode } from "react";
import { moneyExactSigned } from "../../lib/money";
import { liveOn } from "../../lib/liveFire";
import { todayIsoAppTz } from "../../lib/apptime";
import {
  CONTRACTOR_MSG, CONTRACTOR_TYPES, PAY_METHODS, PD_FIELDS,
  allContractorsRaw, contractTotal, contractorToRaw,
  narrowContractor, pdSummary, splitInstallments,
  type Contractor, type ContractorPayInfo,
} from "./api";

const money = (n: number): string => moneyExactSigned(n);
/** v1 date stamp for payments: ISO from <input type=date> → "Jul 19, 26". */
const isoToDisplay = (iso: string): string => {
  if (!iso) return new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" });
  const d = new Date(iso + "T00:00:00");
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" });
};
/** Eastern, not UTC — toISOString() rolls the date over at 8pm ET, so a
 * payment logged in the evening was stamped tomorrow. */
const todayIso = (): string => todayIsoAppTz();

export function ContractorsSection({
  address,
  orgSettings,
  onSave,
  onPostExpense,
  canWrite = true,
  probing = false,
}: {
  address: string;
  orgSettings: Record<string, unknown>;
  /** writes the whole TXN_CONTRACTORS array (settings door); undefined-safe */
  onSave: (all: Record<string, unknown>[]) => void;
  /** optional: post a contractor payment into the deal's expense ledger */
  onPostExpense?: (address: string, exp: { desc: string; amount: number; method: string; vendor: string; date: string }) => void;
  /** the DEAL write gate — the one that decides whether onSave does anything */
  canWrite?: boolean;
  /** that gate is still being probed; not yet known to be closed */
  probing?: boolean;
}) {
  // BOTH gates, deliberately — same fix as TxnExpensesSection, which is
  // rendered directly below this section in the same tab and got it first.
  // This section consulted only liveOn(), while the onSave it calls was gated
  // in DealsPage on the deal write-probe (`canEdit ? onSaveContractors :
  // noEdit`). canEditDeals starts null and the probe is allowed 12s, so in that
  // window "+ Add Vendor" / "+ Payment" / "+ CO" / the pay-info pill / remove ✕
  // all rendered fully live and threw the input away on submit — no row, no
  // error, no queued op. 2026-07-27, Tom: "i tried adding twice and it didnt do
  // anything." That report was fixed for expenses only.
  const canEdit = canWrite && liveOn("contractorsEdit");
  const [adding, setAdding] = useState(false);
  /** the open Add-Vendor form's submit — the header button drives it (2026-09-14) */
  const vendorSubmitRef = useRef<(() => void) | null>(null);
  const [payFor, setPayFor] = useState<number | null>(null); // full-array index
  const [coFor, setCoFor] = useState<number | null>(null);
  const [payInfoFor, setPayInfoFor] = useState<number | null>(null);

  // deal's contractors WITH their index in the full array (needed to save without
  // clobbering other deals). Recomputed from orgSettings each render.
  const { entries, kpis } = useMemo(() => {
    const all = allContractorsRaw(orgSettings);
    const want = normKey(address);
    const wantStreet = streetSeg(want);
    const es: { c: Contractor; idx: number }[] = [];
    all.forEach((r, idx) => {
      const c = narrowContractor(r);
      const dk = normKey(c.deal);
      if (dk === want || (wantStreet && streetSeg(dk) === wantStreet)) es.push({ c, idx });
    });
    // v1 tiles: Active = paid < total · Completed = paid ≥ total (total > 0) ·
    // Quoted = Σ totals · Vendors = count.
    const cs = es.map((e) => e.c);
    return {
      entries: es,
      kpis: {
        active: cs.filter((c) => c.paid < contractTotal(c)).length,
        completed: cs.filter((c) => c.paid >= contractTotal(c) && contractTotal(c) > 0).length,
        quoted: cs.reduce((n, c) => n + contractTotal(c), 0),
        vendors: cs.length,
      },
    };
  }, [orgSettings, address]);

  const saveMutation = (mutate: (all: Record<string, unknown>[]) => void) => {
    const all = allContractorsRaw(orgSettings).map((r) => ({ ...r }));
    mutate(all);
    onSave(all);
  };

  const addContractor = (c: Contractor) => {
    saveMutation((all) => { all.push(contractorToRaw({ ...c, deal: address })); });
    setAdding(false);
  };

  // v1 delVendor: one-click splice (no confirm in v1 here).
  const removeVendor = (idx: number) =>
    saveMutation((all) => { if (idx >= 0 && idx < all.length) all.splice(idx, 1); });

  /** v1 submitPayment: log the payment, bump paid, optionally settle a picked
   * installment, and mirror a PAID row into the deal's expenses. */
  const logPayment = (idx: number, amount: number, method: string, dateIso: string, notes: string, instIdx: number | null) => {
    let who = "";
    let instTag = "";
    const date = isoToDisplay(dateIso);
    saveMutation((all) => {
      const c = narrowContractor(all[idx]);
      who = c.name;
      c.payments.unshift({ date, amount, method, notes });
      c.paid = c.paid + amount;
      if (instIdx !== null) {
        const inst = c.installments[instIdx];
        if (inst && !inst.paid) { inst.paid = true; instTag = " — installment " + (instIdx + 1); }
      }
      all[idx] = contractorToRaw(c);
    });
    onPostExpense?.(address, { desc: `${who || "Contractor"} payment${method ? " (" + method + ")" : ""}${instTag}`, amount, method, vendor: who, date });
    setPayFor(null);
  };

  // v1 openChangeOrder: a change order MUTATES the contract total (v.total += amt)
  // AND is kept as a history row. contractTotal(c) === c.total afterward.
  const addChangeOrder = (idx: number, amount: number, reason: string) => {
    saveMutation((all) => {
      const c = narrowContractor(all[idx]);
      c.total = c.total + amount;
      c.changeOrders.push({ amount, reason });
      all[idx] = contractorToRaw(c);
    });
    setCoFor(null);
  };
  const savePayInfo = (idx: number, pay: ContractorPayInfo | null) => {
    saveMutation((all) => {
      const c = narrowContractor(all[idx]);
      c.pay = pay;
      all[idx] = contractorToRaw(c);
    });
    setPayInfoFor(null);
  };

  return (
    <div className="pdcard ctrsec">
      <div className="ctrsechead">
        <h3 className="ctrsectitle">👷 Contractors &amp; Payments</h3>
        {canEdit ? (
          <button className="btn slim green" onClick={() => { if (adding) vendorSubmitRef.current?.(); else setAdding(true); }}
            title={adding ? "Save the vendor you're filling in" : "Add a vendor to this deal"}>
            {adding ? "✓ Save Vendor" : "+ Add Vendor"}
          </button>
        ) : null}
      </div>
      {!canEdit ? (
        <div className="setgate">
          {probing
            ? "Checking write access… saves are not live yet — give it a moment."
            : CONTRACTOR_MSG.editPhaseB}
          {" — this shows your current contractor budget."}
        </div>
      ) : null}

      {/* v1 stat tiles: Active / Completed / Quoted / Vendors */}
      <div className="ctrkpis">
        <div className="ctrkpi"><div className="fld-l">⛑ Active</div><div className="ctrkpival">{kpis.active}</div></div>
        <div className="ctrkpi"><div className="fld-l">✓ Completed</div><div className="ctrkpival good">{kpis.completed}</div></div>
        <div className="ctrkpi"><div className="fld-l">$ Quoted</div><div className="ctrkpival blue">{money(kpis.quoted)}</div></div>
        <div className="ctrkpi"><div className="fld-l">👥 Vendors</div><div className="ctrkpival">{kpis.vendors}</div></div>
      </div>

      {adding && canEdit ? <AddVendorForm onCancel={() => { setAdding(false); vendorSubmitRef.current = null; }} onSave={addContractor} address={address} registerSubmit={(fn) => { vendorSubmitRef.current = fn; }} /> : null}

      {entries.length ? (
        <div className="vendlist">
          {entries.map(({ c, idx }) => {
            const ct = contractTotal(c);
            const pct = ct > 0 ? Math.round((c.paid / ct) * 100) : 0;
            const remaining = Math.max(ct - c.paid, 0);
            const st = c.paid >= ct && ct > 0 ? "Completed" : c.paid > 0 ? "In Progress" : "Not started";
            const stCls = st === "Completed" ? "done" : st === "In Progress" ? "prog" : "not";
            return (
              <div key={idx} className="vendcard">
                <div className="vendhead">
                  <div className="vendpills">
                    <span className="vendname">{c.name || "Contractor"}</span>
                    {c.type ? <span className="vpill typ">{c.type}</span> : null}
                    <span className={"vpill st-" + stCls}>{st}</span>
                    <button
                      className={"vpill pay" + (c.pay?.method ? "" : " unset")}
                      onClick={() => canEdit && setPayInfoFor(payInfoFor === idx ? null : idx)}
                      title={canEdit ? "Payment info" : CONTRACTOR_MSG.editPhaseB}
                    >
                      💳 {c.pay?.method ? pdSummary(c.pay) || c.pay.method : "payment info"}
                    </button>
                  </div>
                  {canEdit ? <button className="rowbtn del" title="Remove vendor" onClick={() => removeVendor(idx)}>✕</button> : null}
                </div>
                <div className="vendpct"><span>{pct}%</span><span>{money(c.paid)} / {money(ct)}</span></div>
                <div className="vendbar"><div className="vendfill" style={{ width: Math.min(pct, 100) + "%" }} /></div>
                <div className="vendrem">Remaining: {money(remaining)}</div>

                {c.payments.length ? (
                  <div className="vendpays">
                    {c.payments.map((p, i) => (
                      <div key={i} className="vendpayrow">
                        <span>✓ {p.date || "—"}{p.method ? " · " + p.method : ""}{p.notes ? <em className="sub"> · {p.notes}</em> : null}</span>
                        <b>{money(p.amount)}</b>
                      </div>
                    ))}
                  </div>
                ) : null}

                {canEdit ? (
                  <>
                    {payFor === idx ? (
                      <PaymentForm
                        installments={c.installments}
                        defaultMethod={c.pay?.method ?? ""}
                        onCancel={() => setPayFor(null)}
                        onSave={(a, m, d, n, ii) => logPayment(idx, a, m, d, n, ii)}
                      />
                    ) : null}
                    {coFor === idx ? <ChangeOrderForm onCancel={() => setCoFor(null)} onSave={(a, r) => addChangeOrder(idx, a, r)} /> : null}
                    {payInfoFor === idx ? <PayInfoForm initial={c.pay} onCancel={() => setPayInfoFor(null)} onSave={(pay) => savePayInfo(idx, pay)} /> : null}
                    {payFor !== idx && coFor !== idx && payInfoFor !== idx ? (
                      <div className="vendacts">
                        <button className="btn slim green" onClick={() => setPayFor(idx)}>+ Payment</button>
                        <button className="btn slim" onClick={() => setCoFor(idx)}>+ CO</button>
                      </div>
                    ) : null}
                  </>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="vendempty">No contractors or vendors yet.</div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ forms -- */

/** v1 Add-Vendor modal: name, type, quote total, installment plan (checkbox +
 * count), notes, and the payment method + its details. */
function AddVendorForm({ address, onCancel, onSave, registerSubmit }: { address: string; onCancel: () => void; onSave: (c: Contractor) => void; registerSubmit?: (fn: () => void) => void }) {
  const [name, setName] = useState("");
  const [type, setType] = useState("");
  const [total, setTotal] = useState("");
  const [desc, setDesc] = useState("");
  const [plan, setPlan] = useState(false);
  const [installN, setInstallN] = useState("");
  const [method, setMethod] = useState("");
  const [details, setDetails] = useState<Record<string, string>>({});
  const fields = PD_FIELDS[method] ?? [];
  const setField = (k: string, v: string) => setDetails((d) => ({ ...d, [k]: v }));
  const t = Number(total.replace(/[^0-9.]/g, ""));
  const n = parseInt(installN.replace(/[^0-9]/g, ""), 10);
  const ok = name.trim() !== "" && Number.isFinite(t) && t > 0;
  const submit = () => {
    const out: Record<string, string> = {};
    for (const [k] of fields) { const v = (details[k] ?? "").trim(); if (v) out[k] = v; }
    onSave({
      deal: address, name: name.trim(), type: type.trim(), desc: desc.trim(), total: t, paid: 0,
      payments: [],
      installments: plan && Number.isFinite(n) && n > 0 ? splitInstallments(t, n) : [],
      changeOrders: [],
      pay: method ? { method, details: out } : null,
    });
  };
  registerSubmit?.(() => { if (ok) submit(); });
  return (
    <Form title="Add Vendor" onCancel={onCancel} onSubmit={ok ? submit : undefined} submitLabel="Add Vendor">
      <input className="ctrinput" placeholder="Vendor name *" value={name} onChange={(e) => setName(e.target.value)} />
      <input className="ctrinput" placeholder="Type (e.g. Plumber)" list="ctr-types" value={type} onChange={(e) => setType(e.target.value)} />
      <input className="ctrinput" placeholder="Quote / contract total ($) *" inputMode="decimal" value={total} onChange={(e) => setTotal(e.target.value)} />
      <label className="ctrplan">
        <input type="checkbox" checked={plan} onChange={(e) => setPlan(e.target.checked)} />
        Installment plan
        {plan ? <input className="ctrinput slimn" placeholder="# payments" inputMode="numeric" value={installN} onChange={(e) => setInstallN(e.target.value)} /> : null}
      </label>
      <input className="ctrinput" placeholder="Notes (optional)" value={desc} onChange={(e) => setDesc(e.target.value)} />
      <select className="ctrinput" value={method} onChange={(e) => { setMethod(e.target.value); }}>
        <option value="">Payment method (optional)</option>
        {PAY_METHODS.map((x) => <option key={x} value={x}>{x}</option>)}
      </select>
      {fields.map(([k, label, ph]) => (
        <input key={k} className="ctrinput" placeholder={label + (ph ? ` — ${ph}` : "")} value={details[k] ?? ""} onChange={(e) => setField(k, e.target.value)} />
      ))}
      <datalist id="ctr-types">{CONTRACTOR_TYPES.map((x) => <option key={x} value={x} />)}</datalist>
    </Form>
  );
}

function ChangeOrderForm({ onCancel, onSave }: { onCancel: () => void; onSave: (amount: number, reason: string) => void }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const a = Number(amount.replace(/[^0-9.-]/g, ""));
  const ok = Number.isFinite(a) && a !== 0;
  return (
    <Form title="Change Order" onCancel={onCancel} onSubmit={ok ? () => onSave(a, reason.trim()) : undefined} submitLabel="Add change order">
      <input className="ctrinput" placeholder="CO amount ($, negative to reduce)" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
      <input className="ctrinput" placeholder="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
    </Form>
  );
}

function PayInfoForm({ initial, onCancel, onSave }: { initial: ContractorPayInfo | null; onCancel: () => void; onSave: (pay: ContractorPayInfo | null) => void }) {
  const [method, setMethod] = useState(initial?.method ?? "");
  const [details, setDetails] = useState<Record<string, string>>({ ...(initial?.details ?? {}) });
  const fields = PD_FIELDS[method] ?? [];
  const setField = (k: string, v: string) => setDetails((d) => ({ ...d, [k]: v }));
  const submit = () => {
    // keep only the chosen method's declared, non-blank fields (v1 _pdCollect)
    const out: Record<string, string> = {};
    for (const [k] of fields) { const v = (details[k] ?? "").trim(); if (v) out[k] = v; }
    onSave(method || Object.keys(out).length ? { method, details: out } : null);
  };
  return (
    <Form title="Payment info" onCancel={onCancel} onSubmit={submit} submitLabel="Save">
      <select className="ctrinput" value={method} onChange={(e) => setMethod(e.target.value)}>
        <option value="">Select method</option>
        {PAY_METHODS.map((x) => <option key={x} value={x}>{x}</option>)}
      </select>
      {fields.map(([k, label, ph]) => (
        <input key={k} className="ctrinput" placeholder={label + (ph ? ` — ${ph}` : "")} value={details[k] ?? ""} onChange={(e) => setField(k, e.target.value)} />
      ))}
      {method === "" ? <div className="sub">Pick a method to enter payout details.</div> : null}
    </Form>
  );
}

/** v1 Add-Payment modal: pick an unpaid installment to fill the amount (and
 * settle it on save), amount, date (defaults today), method (prefilled from
 * the vendor's payment info), notes. */
function PaymentForm({ installments, defaultMethod, onCancel, onSave }: {
  installments: { amount: number; paid: boolean }[];
  defaultMethod: string;
  onCancel: () => void;
  onSave: (amount: number, method: string, dateIso: string, notes: string, instIdx: number | null) => void;
}) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState(defaultMethod);
  const [date, setDate] = useState(todayIso());
  const [notes, setNotes] = useState("");
  const [instIdx, setInstIdx] = useState<number | null>(null);
  const unpaid = installments.map((p, ii) => ({ p, ii })).filter((x) => !x.p.paid);
  const a = Number(amount.replace(/[^0-9.]/g, ""));
  const ok = Number.isFinite(a) && a > 0;
  const pick = (ii: number, amt: number) => {
    if (instIdx === ii) { setInstIdx(null); return; }
    setInstIdx(ii);
    setAmount(String(amt));
  };
  return (
    <Form title="Add Payment" onCancel={onCancel} onSubmit={ok ? () => onSave(a, method.trim(), date, notes.trim(), instIdx) : undefined} submitLabel="Add Payment">
      {unpaid.length ? (
        <div className="payinstpick">
          <div className="fld-l">From installment plan</div>
          <div className="payinstchips">
            {unpaid.map(({ p, ii }) => (
              <button key={ii} type="button" className={"instchip" + (instIdx === ii ? " on" : "")} onClick={() => pick(ii, p.amount)}>
                #{ii + 1} · {money(p.amount)}
              </button>
            ))}
          </div>
          <div className="sub">Pick an installment to fill the amount — or type a custom amount below.</div>
        </div>
      ) : null}
      <input className="ctrinput" placeholder="Amount ($) *" inputMode="decimal" value={amount} onChange={(e) => { setAmount(e.target.value); setInstIdx(null); }} />
      <input className="ctrinput" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      <select className="ctrinput" value={method} onChange={(e) => setMethod(e.target.value)}>
        <option value="">Select method (optional)</option>
        {PAY_METHODS.map((x) => <option key={x} value={x}>{x}</option>)}
      </select>
      <input className="ctrinput" placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
    </Form>
  );
}

function Form({ title, children, onCancel, onSubmit, submitLabel }: { title: string; children: ReactNode; onCancel: () => void; onSubmit: (() => void) | undefined; submitLabel: string }) {
  return (
    <div className="ctrform">
      <div className="ctrformtitle">{title}</div>
      {children}
      <div className="ctrformacts">
        <button className="btn slim primary" disabled={!onSubmit} onClick={onSubmit}>{submitLabel}</button>
        <button className="btn slim" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

/* address helpers — natKey-equivalent inline to avoid a cross-import cycle */
const normKey = (s: string): string => String(s ?? "").trim().toLowerCase();
const streetSeg = (k: string): string => (k.split(",")[0] ?? "").trim();
